<template>
  <section
    id="capability-chain"
    ref="revealTarget"
    class="section capability-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="capability-backdrop" aria-hidden="true">
      <ui-threads :amplitude="2" :color="threadsColor" :distance="0.16" enable-mouse-interaction />
    </div>
    <div class="page-container">
      <div class="section-heading-row">
        <div class="section-head">
          <span class="section-kicker">INNOVATION PIPELINE</span>
          <h2>从科研创新到产品产业化</h2>
          <p>围绕热带特色植物资源，以研发、中试、成果转化和生产服务形成可落地的产业能力。</p>
        </div>
        <nuxt-link class="section-more" to="/company/strength">
          查看更多
          <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
          <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
        </nuxt-link>
      </div>

      <div class="capability-track">
        <article v-for="(item, index) in capabilities" :key="item.code" class="capability-card">
          <div class="capability-card-top">
            <span class="capability-card-index">{{ String(index + 1).padStart(2, '0') }} ·</span>
            <span>{{ item.code }}</span>
          </div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  defineOptions({
    name: 'HomeCapability',
  })

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const threadsColor: [number, number, number] = [1, 0.76, 0.78]

  const capabilities = [
    {
      code: 'RESEARCH',
      title: '科研创新',
      description: '围绕种质、栽培、病虫害与加工等方向开展科研。',
    },
    {
      code: 'PILOT',
      title: '中实验证',
      description: '建立从研究成果到生产环节之间的验证桥梁。',
    },
    {
      code: 'TRANSFER',
      title: '成果转化',
      description: '形成产品、工艺、标准与技术服务成果。',
    },
    {
      code: 'INDUSTRY',
      title: '产品产业化',
      description: '面向企业、合作社与农户提供持续产业支持。',
    },
    {
      code: 'SERVICE',
      title: '技术服务',
      description: '面向企业、合作社与农户提供持续产业支持。',
    },
  ]
</script>

<style scoped lang="scss">
  .capability-section {
    position: relative;
    overflow: hidden;
    color: var(--white);
    background: #92000b;
    isolation: isolate;
  }

  .capability-backdrop {
    position: absolute;
    inset: auto 0 -18%;
    z-index: 0;
    height: 62%;
    pointer-events: none;
    opacity: 0.62;
  }

  .capability-backdrop :deep(.threads-root) {
    min-height: 100%;
  }

  .capability-section > .page-container {
    position: relative;
    z-index: 1;
  }

  .section-heading-row {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 82px;
  }

  .section-head {
    max-width: 780px;
    margin-bottom: 0;
  }

  .section-head h2 {
    margin: 0;
    font-size: clamp(30px, 3.5vw, 48px);
    font-weight: 620;
    line-height: 1.18;
  }

  .section-head p {
    max-width: 68ch;
    margin: 14px 0 0;
    color: rgb(255 255 255 / 78%);
  }

  .section-kicker {
    display: inline-flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
    font:
      650 12px/1.4 ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    color: rgb(255 255 255 / 86%);
    letter-spacing: 0.1em;
  }

  .section-kicker::before {
    width: 28px;
    height: 2px;
    content: '';
    background: currentColor;
  }

  .section-more {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 8px;
    align-items: center;
    margin-top: 56px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--white);
    white-space: nowrap;
    cursor: pointer;
  }

  .section-more i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .section-more:hover {
    color: var(--white);
  }

  .section-more:hover i {
    transform: translateX(4px);
  }

  .capability-track {
    position: relative;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    padding-top: 44px;
  }

  .capability-track::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 1px;
    content: '';
    background: rgb(255 255 255 / 72%);
  }

  .capability-card {
    position: relative;
    min-height: 170px;
    padding: 0 32px;
    background: transparent;
  }

  .capability-card::before {
    position: absolute;
    top: -49px;
    left: 0;
    width: 10px;
    height: 10px;
    content: '';
    background: var(--white);
    border-radius: 50%;
    transform: translateX(-50%);
  }

  .capability-card:not(:last-child)::after {
    position: absolute;
    top: -44px;
    right: 0;
    bottom: 0;
    width: 1px;
    content: '';
    background: rgb(255 255 255 / 60%);
  }

  .capability-card:last-child {
    padding-right: 0;
  }

  .capability-card-top {
    display: flex;
    gap: 8px;
    align-items: center;
    font: 500 14px/1.2 var(--num);
    color: rgb(255 255 255 / 92%);
    letter-spacing: 0.08em;
  }

  .capability-card-index {
    color: rgb(255 255 255 / 72%);
  }

  .capability-card h3 {
    margin: 20px 0 12px;
    font-size: 24px;
    font-weight: 620;
    line-height: 1.3;
  }

  .capability-card p {
    margin: 0;
    font-size: 14px;
    line-height: 1.8;
    color: rgb(255 255 255 / 78%);
  }

  @media (max-width: 767px) {
    .section-heading-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px 16px;
      align-items: center;
      margin-bottom: 36px;
    }

    .section-head {
      display: contents;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      min-width: 0;
      margin-bottom: 0;
    }

    .section-head h2 {
      grid-row: 2;
      grid-column: 1 / -1;
      font-size: 28px;
      line-height: 1.25;
    }

    .section-head p {
      grid-row: 3;
      grid-column: 1 / -1;
      margin: 0;
    }

    .section-more {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      margin: 0;
    }

    .capability-backdrop {
      inset: auto -24% -14%;
      height: 62%;
      opacity: 0.48;
    }

    .capability-track {
      --capability-card-width: 78vw;
      --capability-track-gutter: calc((100vw - var(--capability-card-width)) / 2);

      display: flex;
      gap: 0;
      padding: 6px var(--capability-track-gutter) 8px;
      margin-inline: -24px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-padding-inline: var(--capability-track-gutter);
      scrollbar-width: none;
      border: 0;
      border-radius: 0;
      box-shadow: none;
    }

    .capability-track::before {
      display: none;
    }

    .capability-track::-webkit-scrollbar {
      display: none;
    }

    .capability-card,
    .capability-card:nth-child(-n + 2) {
      flex: 0 0 var(--capability-card-width);
      min-height: 188px;
      padding: 42px 20px 18px;
      scroll-snap-align: center;
      scroll-snap-stop: always;
      border: 0;
      border-top: 1px solid rgb(255 255 255 / 72%);
      border-left: 1px solid rgb(255 255 255 / 60%);
      border-radius: 0;
    }

    .capability-card:first-child {
      padding-left: 28px;
    }

    .capability-card:last-child {
      padding-right: 28px;
      border-right: 1px solid rgb(255 255 255 / 60%);
    }

    .capability-card::before {
      top: 0;
      width: 8px;
      height: 8px;
      transform: translate(-50%, -50%);
    }

    .capability-card:not(:last-child)::after {
      display: none;
    }

    .capability-card h3 {
      margin-top: 14px;
      font-size: 20px;
    }

    .capability-card p {
      line-height: 1.7;
    }
  }
</style>
